import { InjectionToken } from '@angular/core';
import { MitigationsService } from '../shared/mitigations/mitigations-service';
import { MaxUploadSizes } from './max-upload-sizes';

declare let require: any;

/**
 * Configuration class contains data read from the assets/config.config.json file
 * Use this for things that need to be editable after compilation, e.g. api base URLs
 */
export class Configuration {
  /**
   * Dictionary of feature toggles
   */
  public features: { [key: string]: boolean } = {};

  constructor(
    public apiBaseUrl: string,
    public authBaseUrl: string,
    public redirectUrl: string,
    public identityUrl: string,
    public postLogoutUrl: string,
    public aboutItems: any[],
    /**
     * Maximum file upload sizes in MB
     */
    public maxUploadSizes: MaxUploadSizes,
    public provider: string,
    public expirationtime: number
  ) { }
}

/**
 * Hard-coded config that does not change with environment, such as routes within the api
 */
export const CONFIG = {
  apiRoutes: {
    applicationConfig: 'applicationConfig',
    lookup: 'staticlookup',
    lookupDisplayValue: 'staticlookup/displayValue',
    lookupAsync: 'staticlookupasync',
    model: 'models',
    modelSummary: 'models/summaries',
    rbiModel: 'rbiModel',
    rcmModel: 'rcmModel',
    rcmLibraries: 'rcmModel/libraries',
    rcmPublish: 'rcmModel/publish',
    industryHierarchy: 'industryHierarchy',
    business: 'business',
    batchJobHistory: 'batchjobhistory',
    installation: 'installation',
    plantUnit: 'plantUnit',
    systemSection: 'systemSection',
    equipmentClass: 'equipmentClass',
    equipmentClassType: 'equipmentClassType',
    ValidateEquipmentClassType: 'equipmentClassType/Validate',
    equipmentLibrary: 'equipmentLibrary',
    equipmentSubUnit: 'equipmentSubUnit',
    equipmentComponent: 'equipmentComponent',
    equipmentFunction: 'equipmentFunction',
    equipmentFunctionModel: 'equipmentFunction/model',
    equipmentFunctionalFailure: 'equipmentFunction/functionalFailure',
    plannedMaintenance: 'equipmentFunction/plannedMaintenance',
    plannedMaintenanceLibrary: 'plannedMaintenanceLibrary',
    equipmentFailureMode: 'equipmentFunction/failureMode',
    failureMechanism: 'equipmentFunction/failureMechanism',
    failureMechanismReason: 'equipmentFunction/failureMechanismReason',
    failureCause: 'equipmentFunction/failureCause',
    equipmentFailureEffect: 'equipmentFunction/failureEffect',
    equipmentFunctionPlannedMaintenance: 'equipmentFunction/plannedMaintenance',
    equipmentFailureEffectCommercialImpact: 'equipmentFailureEffectCommercialImpact',
    commercialImpactItem: 'commercialImpactItem',
    failureRateSelectionCriteria: 'failureRateSelectionCriteria',
    activateMaintenanceAnalysis: 'maintenanceAnalysis/activate',
    maintenanceAnalysis: 'maintenanceAnalysis',
    maintenanceAnalysisByOrgTreeNode: 'maintenanceAnalysis/orgTreeNode',
    maintenanceAnalysisItems: 'maintenanceAnalysis/history',
    maintenanceAnalysisContributor: 'maintenanceAnalysisContributor',
    maintenanceAnalysisFunction: 'maintenanceAnalysis/function',
    maintenanceAnalysisFunctionalFailure: 'maintenanceAnalysis/functionalFailure',
    maintenanceAnalysisFailureMode: 'maintenanceAnalysis/failureMode',
    maintenanceAnalysisFailureMechanism: 'maintenanceAnalysis/failureMechanism',
    maintenanceAnalysisFailureMechanismReason: 'maintenanceAnalysis/failureMechanismReason',
    maintenanceAnalysisFailureCause: 'maintenanceAnalysis/failureCause',
    maintenanceAnalysisFailureEffect: 'maintenanceAnalysis/failureEffect',
    maintenanceAnalysisModelFunctions: 'maintenanceAnalysis/modelFunctions',
    maintenanceAnalysisPlannedMaintenance: 'maintenanceAnalysis/plannedMaintenance',
    maintenanceOptimization: 'maintenanceoptimization',
    maintenanceOptimizationAdHoc: 'maintenanceoptimization/adhoc',
    maintenanceOptimizationAdHocMaintenance: 'maintenanceoptimization/adhoc/maintenance',
    maintenanceOptimizationCostInterval: 'maintenanceOptimization/costInterval',
    maintenanceOptimizationCostSensitivity: 'maintenanceOptimization/costSensitivity',
    maintenanceOptimizationEquipment: 'maintenanceOptimization/equipment',
    maintenanceOptimizationListing: 'maintenanceoptimization/listings',
    maintenanceOptimizationLoop: 'maintenanceoptimization/loops',
    maintenanceOptimizationPmsByAnalysisIds: 'maintenanceoptimization/plannedMaintenanceByAnalysis',
    maintenanceOptimizationPOFOD: 'maintenanceOptimization/probabilityOfFailureOnDemand',
    maintenanceOptimizationTargetInterval: 'maintenanceOptimization/targetInterval',
    maintenanceOptimizationTargetSensitivity: 'maintenanceOptimization/targetSensitivity',
    optimizationAssociatedEquipment: 'OptimizationAssociatedEquipment',
    optimizationAssociatedEquipmentUpdate: 'OptimizationAssociatedEquipment/Update',
    objectDrawerFilter: 'objectDrawerFilter',
    objectDrawerFilterHierarchy: 'objectDrawerFilter/hierarchy',
    objectDrawerFilterInspectionReports: 'objectDrawerFilter/inspectionReports',
    objectDrawerFilterMaintenanceStrategy: 'objectDrawerFilter/maintenanceStrategy',
    objectDrawerFilterMassCriticality: 'objectDrawerFilter/mass-criticality-results',
    objectDrawerFilterMitigation: 'objectDrawerFilter/mitigation',
    objectDrawerFilterThicknessReports: 'objectDrawerFilter/thicknessReports',
    objectDrawerFilterRbiRiskAnalysis: 'objectDrawerFilter/rbi-risk-analysis',
    objectDrawerFilterThicknessLocation: 'objectDrawerFilter/thicknessLocations',
    equipmentPart: 'equipmentPart',
    maintenanceCalculation: 'maintenanceoptimization/calculate',
    modelNodeLayout: 'modelnodelayout',
    modelNode: 'modelnodes',
    node: 'nodes',
    nodeAttributes: 'nodeattributes',
    nodeInput: 'nodeinputs',
    nodeInputSyncMappingOptions: 'syncalgorithmsmodelmappings/mappingOptions',
    nodeOutput: 'nodeoutputs',
    nodeProcess: 'nodeprocesses',
    internalNodeInputMapping: 'internalnodeinputmapping',
    databaseEnvironment: 'databaseenvironment',
    runnableModel: 'runnablemodel',
    runnableModelDropdownOptions: 'runnablemodel/dropdownoptions',
    runnableModelMetadata: 'runnablemodelmetadata',
    runnableModelSummaryData: 'runnablemodelmetadata',
    runnableModelMetadataGetEstimatedHalfLifeOutputMapping: 'runnablemodelmetadata/GetRunnableModelEstimatedHalfLifeOutputMapping',
    runnableModelMetadataUpdateEstimatedHalfLifeOutputMapping: 'runnablemodelmetadata/UpdateRunnableModelEstimatedHalfLifeOutputMapping',
    runnableModelMetadataUpdateRunOrder: 'runnablemodelmetadata/UpdateRunOrder',
    runnableModelFrlSettings: 'runnablemodelmetadata/frlsettings',
    runnableModelMetadataGetNumericNodeOutputs: 'runnablemodelmetadata/GetNumericRunnableModelNodeOutputs',
    buildMitigations: 'build/mitigations',
    buildMitigationColumns: 'build/mitigations/columns',
    buildMitigationRows: 'build/mitigations/rows',
    buildMitigationCells: 'build/mitigations/cells',
    buildMitigationMappings: 'build/mitigations/mappings',
    buildMitigationInspectionPriorityModelMappings: 'build/mitigations/inspectionprioritymodelmapping',
    adminMitigations: 'admin/mitigations',
    adminMitigationColumns: 'admin/mitigations/columns',
    adminMitigationRows: 'admin/mitigations/rows',
    adminMitigationCells: 'admin/mitigations/cells',
    adminMitigationMappings: 'admin/mitigations/adminMappings',
    adminMitigationInspectionPriorityModelMappings: 'admin/mitigations/inspectionprioritymodelmapping',
    adminOrganization: 'admin/organisation',
    adminSite: 'admin/site',
    function: 'functions',
    functionInput: 'functioninputs',
    functionOutput: 'functionoutputs',
    authorization: 'authorization',
    adminUnit: 'admin/units',
    adminUnitLookup: 'admin/units/lookup',
    uomConversions: 'admin/uomconversions',
    uomConvert: 'admin/uomconvert',
    lookups: 'lookups',
    lookupColumns: 'lookupcolumns',
    lookupRows: 'lookuprows',
    lookupCells: 'lookupcells',
    modelOrgLinks: 'modelorglink',
    modelOrgLinksList: 'modelorglink/links/model/paginate',
    modelOrgLinksListCount: 'modelorglink/links/model/getCount',
    modelOrgLinksListDetails: 'modelorglink/link-details/model',
    modelOrgLinksMedia: 'modelorglink/media',
    modelOrgLinksMediaFiltered: 'modelorglink/media/filtered',
    modelOrgLinksMediaFlag: 'modelorglink/mediaFlag',
    modelOrgLinksAssetHubConfig: 'modelOrgLink/getAssetHubConfig',
    moAssetHubDetails: 'maintenanceOptimisationAssetHub/details',
    moAssetHubMediaItems: 'maintenanceOptimisationAssetHub/media',
    moAssetHubRiskMatrix: 'maintenanceOptimisationAssetHub/riskMatrix',
    moAssetHubPlannedMaintenance: 'maintenanceOptimisationAssetHub/planned-maintenance',
    moAssetHubPlannedMaintenanceChildren: 'maintenanceOptimisationAssetHub/planned-maintenance-children',
    massCriticalityRun: 'massCriticalityRun',
    massCriticalityResults: 'massCriticalityResults',
    massCriticalityResultsPreview: 'massCriticalityResults/preview',
    massCriticalityResultModelOptions: 'massCriticalityResults/modelOptions',
    massCriticalityResultsOverview: 'massCriticalityResults/overview',
    massCriticalityPreviousDrivingRunComparison: 'massCriticalityResults/previousDrivingRunComparison',
    massCriticalityRiskMatrixComparison: 'massCriticalityResults/riskMatrixComparison',
    massCriticalityTemplates: 'massCriticalityTemplates',
    massCriticalityTemplatesAssets: 'massCriticalityTemplates/assets',
    massCriticalityTemplatesAssetsTotalRun: 'massCriticalityTemplates/assets/totalRun',
    massRiskResultsSummary: 'massCriticalityResults/summary',
    massRiskResultsNotRun: 'massCriticalityResults/notRun',
    masterInspectionMethods: 'masterInspectionMethods',
    masterInspectionMethodsByThicknessReportType: 'masterInspectionMethods/thicknessReportType',
    nodeInputSyncAlgorithms: 'syncAlgorithms',
    nodeInputSyncAlgorithmCalculate: 'syncAlgorithms/calculate',
    simpleParameter: 'simpleParameter',
    recommendations: 'recommendations',
    orphanedReports: 'orphanedReports',
    orphanedReportsUpdate: 'orphanedReports/update',
    orphanedReportsUpdateAll: 'orphanedReports/updateAll',
    riskMatrices: 'riskmatrices',
    calibratedRiskMatrices: 'riskmatrices/calibrated',
    riskMatrixConsequenceVectors: 'riskmatrixconsequencevectors',
    riskMatrixProbabilityVectors: 'riskmatrixprobabilityvectors',
    riskMatrixCells: 'riskmatrixcells',
    riskMatrixCanvases: 'riskmatrixcanvases',
    riskMatrixBands: 'riskmatrixbands',
    factoredRemainingLifeMatrix: 'factoredRemainingLifeCell',
    factoredRemainingLifeCell: 'factoredRemainingLifeCell',
    factoredRemainingLifGeteModelStatus: 'factoredRemainingLifeCell/GetFactoredRemainingLifeModelStatus',
    factoredRemainingLifeUpdateteModelStatus: 'factoredRemainingLifeCell/UpdateFactoredRemainingLifeModelStatus',
    hierarchy: 'hierarchy',
    hierarchyLocation: 'hierarchy/location',
    hierarchyAsset: 'hierarchy/asset',
    hierarchyComponent: 'hierarchy/component',
    hierarchyPart: 'hierarchy/part',
    hierarchyParent: 'hierarchy/parent',
    hierarchyParentProtectingLocations: 'hierarchy/parent/protectinglocations',
    hierarchyParentSite: 'hierarchy/parent/hierarchyItemSite',
    hierarchyStructureInfo: 'hierarchy/parent/hierarchyStructureInfo',
    hierarchyStructureItems: 'hierarchy/parent/hierarchyStructureItems',
    hierarchyTreeviewItems: 'hierarchy/parent/hierarchyTreeItems',
    hierarchyReplacement: 'hierarchy/replacement',
    objectDrawerHierarchyTreeRoot: 'hierarchy/objectDrawerTreeRoot',
    objectDrawerHierarchyTreeChildren: 'hierarchy/objectDrawerTreeChildren',
    objectDrawerHierarchyTreeRootSecurity: 'hierarchy/objectDrawerTreeRootSecurity',
    objectDrawerHierarchyTreeChildrenSecurity: 'hierarchy/objectDrawerTreeChildrenSecurity',
    objectDrawerHierarchyFilteredTreeNodes: 'hierarchy/objectDrawerFilteredTreeNodes',
    objectDrawerInspectionReportTreeRoot: 'inspectionReportTileOptions/objectDrawerTreeRoot',
    objectDrawerInspectionHierarchyTreeChildren: 'inspectionReportTileOptions/hierarchyTreeItems',
    objectDrawerInspectionReportTreeChildren: 'inspectionReportTileOptions/objectDrawerTreeChildren',
    objectDrawerInspectionReportFilteredTreeNodes: 'inspectionReportTileOptions/objectDrawerFilteredTreeNodes',
    objectDrawerModelOrgLinkTreeRoot: 'modelOrgLink/objectDrawerTreeRoot',
    objectDrawerModelOrgLinkTreeChildren: 'modelOrgLink/objectDrawerTreeChildren',
    objectDrawerModelOrgLinkTreeviewItems: 'modelOrgLink/hierarchyTreeItems',
    objectDrawerModelOrgLinkFilteredTreeNodes: 'modelOrgLink/objectDrawerFilteredTreeNodes',
    objectDrawerMitigationTreeChildren: 'recommendations/objectDrawerTreeChildren',
    objectDrawerMitigationHierarchyTreeChildren: 'recommendations/hierarchyTreeItems',
    objectDrawerMitigationTreeRoot: 'recommendations/objectDrawerTreeRoot',
    objectDrawerMitigationFilteredTreeNodes: 'recommendations/objectDrawerFilteredTreeNodes',
    objectDrawerThicknessHierarchyTreeChildren: 'thicknessReportTileOptions/hierarchyTreeItems',
    objectDrawerThicknessReportTreeRoot: 'thicknessReportTileOptions/objectDrawerTreeRoot',
    objectDrawerThicknessReportTreeChildren: 'thicknessReportTileOptions/objectDrawerTreeChildren',
    objectDrawerThicknessReportFilteredTreeNodes: 'thicknessReportTileOptions/objectDrawerFilteredTreeNodes',
    objectDrawerThicknessLocationTreeRoot: 'thicknesslocations/objectDrawerTreeRoot',
    objectDrawerThicknessLocationTreeChildren: 'thicknesslocations/objectDrawerTreeChildren',
    objectDrawerThicknessLocationHierarchyTreeChildren: 'thicknesslocations/hierarchyTreeItems',
    objectDrawerThicknessLocationFilteredTreeNodes: 'thicknesslocations/objectDrawerFilteredTreeNodes',
    objectDrawerMaintenanceStrategyTreeRoot: 'maintenanceStrategyTileOptions/objectDrawerTreeRoot',
    objectDrawerMaintenanceStrategyTreeChildren: 'maintenanceStrategyTileOptions/objectDrawerTreeChildren',
    objectDrawerMaintenanceStrategyFilteredTreeNode: 'maintenanceStrategyTileOptions/objectDrawerFilteredTreeNodes',
    licence: 'licence',
    modelRiskMatrix: 'modelriskmatrix',
    modelVisualization: 'admin/visualization',
    modelVisualizationNotUsedCategoryNodeOutputs: 'admin/visualization/notUsedNodeOutputs',
    ModelCategoryCalibration: 'admin/modelcategorycalibration',
    riskMatrixCalibration: 'admin/modelcategorycalibration/riskMatrix',
    orgTreeNodeModelRun: 'orgtreenodemodelruns',
    orgTreeNodeModelRunByOrgTreeNode: 'orgtreenodemodelruns/orgtreenode',
    orgTreeNodeModelRunDrivingRunByOrgTreeNode: 'orgtreenodemodelruns/drivingbyorgtreenode',
    orgTreeNodeModelRunIpnsByOrgTreeNodeId: 'orgtreenodemodelruns/ipnsbyorgtreenodeid',
    orgTreeNodeModelRunByOrgTreeNodeModelRunId: 'orgtreenodemodelruns/modelrunbyorgtreenodemodelrunid',
    hierarchyTemplates: 'admin/hierarchyTemplates',
    hierarchyTemplatesTabs: 'admin/hierarchyTemplates/hierarchyTemplateTabsAndGroups',
    hierarchyTemplatesGroups: 'admin/hierarchyTemplates/hierarchyTemplateGroups',
    tableAttributeTemplate: 'admin/tableTemplate',
    hierarchyTemplatesByAssetFamily: 'admin/hierarchyTemplates/assetfamily',
    hierarchyTemplateAttributes: 'admin/hierarchytemplateattributes',
    hierarchyTemplateMitigations: 'admin/hierarchytemplatemitigations',
    hierarchyTemplateWithAttribute: 'admin/hierarchyTemplates/templateAttribute',
    hierarchyTemplateTab: 'admin/hierarchyTemplates/hierarchyTemplateTab',
    inspectionReportTemplates: 'admin/inspectionreporttemplates',
    inspectionReportTemplatesByAssetFamily: 'admin/inspectionreporttemplates/assetfamily',
    inspectionReportTemplateHeaders: 'admin/inspectionreporttemplateheaders',
    inspectionReportTemplateMeasurements: 'admin/inspectionreporttemplatemeasurements',
    inspectionReportTemplateObservations: 'admin/inspectionreporttemplateobservations',
    inspectionReportAvailableMasterInspectionMethods: 'admin/inspectionreporttemplates/availablemasterinspectionmethods',
    assetFamilies: 'assetfamily',
    inspectionReports: 'inspectionreports',
    inspectionReportsOverviewCount: 'inspectionreports/getcount',
    inspectionReportsOverview: 'inspectionreports/getoverviewlist',
    nodeInputMetadata: 'runnableModelMetadata/getNodeInputByMasterAttributeDataType',
    nodeOutputMetadata: 'runnableModelMetadata/getNodeOutputByMasterAttributeDataType',
    inspectionReportsInspectionDate: 'inspectionreports/inspectiondate',
    inspectionReportsByAsset: 'inspectionreports/asset',
    inspectionReportHistory: 'inspectionreports/history',
    inspectionReportWorkOrders: 'inspectionreportworkorders',
    inspectionReportTasks: 'inspectionreporttasks',
    inspectionReportAvailableTasks: 'inspectionReports/getAvailableTasks',
    inspectionReportTemplateWithTasks: 'inspectionReports/getTemplateAndAvailableTasks',
    inspectors: 'admin/inspectors',
    siteChildren: 'hierarchy/sitechildren',
    inspectionPlan: 'inspectionPlan',
    inspectionPlanTask: 'inspectionPlan/task',
    lastActivePlanByMasterInspectionMethod: 'inspectionPlan/lastActivePlanByMasterInspectionMethod',
    inspectionPlanTaskDueDate: 'inspectionPlan/task/due-date',
    inspectionPlanTaskLastInspectionDate: 'inspectionPlan/task/inspection-date',
    inspectionPlanTaskMitigationColumn: 'inspectionPlan/task/mitigation-column',
    previousInspectionPlanTasks: 'inspectionPlan/previousTasks',
    historizedInspectionPlans: 'inspectionPlan/historized',
    thicknessReports: 'thicknessreports',
    thicknessReportLatestByOrgTreeNodeId: 'thicknessreports/getLatestThicknessReportForOrgTreeNode',
    rtThicknessReports: 'rtthicknessreports',
    utThicknessReports: 'utthicknessreports',
    thicknessReportLoadDetails: 'thicknessreports/loadDetails',
    user: 'users',
    securityPrincipal: 'securityprincipal',
    usersByRole: 'users/byRole',
    permissions: 'permissions',
    reseedPermissions: 'securitypermission/reseedPermissions',
    getAllByPermission: 'getAllByPermission',
    ssoProviders: 'users/ssoProviders',
    rolesByUser: 'roles/byUser',
    restrictedRolesByUser: 'roles/restrictedRolesByUser',
    resetUserPassword: 'users/resetPassword',
    logout: 'users/logout',
    userRoles: 'roles',
    restriction: 'hierarchy/sitechildren/restrictions',
    thicknessLocations: 'thicknesslocations',
    thicknessLocationsReadings: 'thicknesslocations/thicknessReadings',
    thicknessLocationsOverview: {
      getThicknessLocationCount: 'thicknessLocationsOverview/getThicknessLocationCount',
      getThicknessLocations: 'thicknessLocationsOverview/getThicknessLocations',
      GetStructuralDetails: 'thicknessLocationsOverview/getStructuralDetails',
      getPressureDetails: 'thicknessLocationsOverview/getPressureDetails',
      updateMultiple: 'thicknessLocationsOverview/updateMultiple',
    },
    currentRecommendations: 'recommendations/currentRecommendations',
    equipment: 'equipment',
    pressureMinimumThicknessCalculation: 'pressureminimumthicknesscalculation',
    currentRbiInspectionPlanByOrgTreeNode: 'recommendations/currentRbiInspectionPlanByOrgTreeNode',
    createInspectionPlanFromCurrent: 'recommendations/createInspectionPlanFromCurrent',
    thicknessLocationsByReport: 'thicknesslocations/reportcandidates',
    decommissionThicknessLocation: 'thicknesslocations/decommission',
    thicknessLocationsByMediaItemDetails: 'thicknesslocations/mediacandidates',
    mediaAttachments: 'mediaattachments',
    mediaItemDetails: 'mediaitemdetails',
    mediaItemDetailsForOrgTreeNodeAssetHub: 'mediaitemdetails/getAssetHubMediaForOrgTreeNode',
    mediaItemDetailsOrgTreeNodeHasMedia: 'mediaItemDetails/orgTreeNodeHasMedia',
    documentDownload: 'documentdownload',
    hierarchicalDataUploadItems: 'dataUploads/hierarchicalItemsDataUploads',
    HierarchicalFileUploadCollections: 'dataUploads/hierarchicalItemsDataUploads/GetPendingFiles',
    heirarchicalDataUploadValidateItems: 'dataUploads/hierarchicalItemsDataUploads/ValidateFileData',
    heirarchicalDataUploadTransferItems: 'dataUploads/hierarchicalItemsDataUploads/TranserFileData',
    hierarchyItemEvaluation: 'hierarchyItemEvaluations',
    modelPackage: 'modelPackage/model',
    modelLookupsPackage: 'modelPackage/modellookups',
    lookupPackage: 'lookups/downloadLookup',
    rcmLibraryPackageDownload: 'modelPackage/rcmdownload',
    rcmLibraryPackageUpload: 'modelPackage/rcmupload',
    reportSelectionCriteria: 'reportselectioncriteria',
    reports: 'report',
    dataSheetReport: 'report/dataSheet',
    reportHierarchyItems: 'reportselectioncriteria',
    uploadDataFile: 'dataMigration',
    pendingDataUploadFiles: 'dataMigration/GetPendingFiles',
    dataUploadTransferData: 'dataMigration/TranserFileData',
    dataUploadValidateData: 'dataMigration/ValidateFileData',
    dataUploadMappableWorksheets: 'dataUploadMappableWorksheets',
    dashboard: 'dashboard',
    deleteRange: 'deleteRange',
    dashboardData: 'dashboard/DashboardData',
    dashboardHierarchyTree: 'dashboard/HierarchyTree',
    hierarchyItemsFilterItemsByRiskBand: 'hierarchyItemsFilter/ItemsByRiskBand',
    hierarchyItemsFilterItemsByRiskMatrix: 'hierarchyItemsFilter/ItemsByRiskMatrix',
    hierarchyItemsFilterItemsByModelId: 'hierarchyItemsFilter/ItemsByModelId',
    hierarchyItemsFilterItemsByModelName: 'hierarchyItemsFilter/ItemsByModelName',
    hierarchyItemsFilterItemsByStatusName: 'hierarchyItemsFilter/ItemsByStatusName',
    hierarchyItemsFilterItemsByErpIdentifier: 'hierarchyItemsFilter/ItemsByErpIdentifier',
    hierarchyItemsFilterItemsByFamilyName: 'hierarchyItemsFilter/ItemsByAssetFamilyName',
    hierarchyItemsFilterItemsByParentName: 'hierarchyItemsFilter/ItemsByParentName',
    hierarchyItemsFilterItemsByTemplateName: 'hierarchyItemsFilter/ItemsByTemplateName',
    templateDownloads: 'dataUploadTemplates',
    erpSettings: 'erpSettings',
    erpSettingsSummary: 'erpSettings/summary',
    erpSettingsTest: 'erpSettings/test',
    erpHierarchySync: 'erpSettings/sync',
    erpSyncHistory: 'erpSyncHistory',
    erpMaintenanceJobs: 'ErpMaintenanceJobs',
    erpSyncCurrentStatus: 'erpSyncHistory/currentStatusInfo',
    erpWorkOrder: 'ErpWorkOrders',
    actionTypes: 'actiontype',

    eventTemplate: 'eventTemplate',
    eventTemplateAttribute: 'eventTemplateAttribute',

    inspectionReportsFilterItemsByConfidenceName: 'inspectionReportsFilter/ReportsByConfidenceName',
    inspectionReportsFilterItemsByOrgTreeNodeName: 'inspectionReportsFilter/ReportsByOrgTreeNodeName',
    thicknessReportsFilterItemsBySourceType: 'inspectionReportsFilter/RtDetailsBySourceType',
    thicknessReportsFilterItemsBySourceStrength: 'inspectionReportsFilter/RtDetailsBySourceStrength',
    thicknessReportsFilterItemsBySourceStrengthUom: 'inspectionReportsFilter/RtDetailsBySourceStrengthUom',
    thicknessReportsFilterItemsByFilmType: 'inspectionReportsFilter/RtDetailsByFilmType',
    thicknessReportsFilterItemsByFilmSize: 'inspectionReportsFilter/RtDetailsByFilmSize',

    thicknessReportsFilterItemsByTestUnit: 'inspectionReportsFilter/UtDetailsByTestUnit',
    thicknessReportsFilterItemsByTransducer: 'inspectionReportsFilter/UtDetailsByTransducer',
    thicknessReportsFilterItemsByCalibrationBlock: 'inspectionReportsFilter/UtDetailsByCalibrationBlock',
    thicknessReportsFilterItemsByManufacturer: 'inspectionReportsFilter/UtDetailsByManufacturer',
    thicknessReportsFilterItemsByFrequency: 'inspectionReportsFilter/UtDetailsByFrequency',
    thicknessReportsFilterItemsByFrequencyUom: 'inspectionReportsFilter/UtDetailsByFrequencyUom',
    thicknessReportsFilterItemsBySerialNumber: 'inspectionReportsFilter/UtDetailsBySerialNumber',
    thicknessReportsFilterItemsByModelNumber: 'inspectionReportsFilter/UtDetailsByModelNumber',
    thicknessReportsFilterItemsByThicknessRange: 'inspectionReportsFilter/UtDetailsByThicknessRange',
    thicknessReportsFilterItemsByUnitSerialNumber: 'inspectionReportsFilter/UtDetailsByUnitSerialNumber',
    thicknessReportsFilterItemsByDiameter: 'inspectionReportsFilter/UtDetailsByDiameter',
    thicknessReportsFilterItemsByDiameterUom: 'inspectionReportsFilter/UtDetailsByDiameterUom',
    thicknessReportsFilterItemsByMaterial: 'inspectionReportsFilter/UtDetailsByMaterial',

    modelInputTemplateMappingSelectionCriteria: 'admin/modelinputtemplatemappingselectioncriteria',
    modelInputTemplateMappings: 'admin/modelinputtemplatemappings',
    modelUnitMappingOrganizations: 'admin/modelunitmappingsorgtreenodeselection',
    modelUnitMappings: 'admin/modelunitmappings',
    flattenedOrgTreeNodes: 'admin/flattenedorgtreenodes',
    runnableLookups: 'admin/runnablelookups',
    runnableLookupsBriefInfo: 'admin/runnableLookups/brief-info',
    runnableLookupsSave: 'admin/runnableLookups/save',
    modelRunnableLookups: 'admin/runnableLookups/model',
    runnableLookupColumns: 'admin/runnablelookupcolumns',
    runnableLookupColumnsMaxColumnIndex: 'admin/runnableLookupColumns/maxColumnIndex',
    RunnableLookupRows: 'admin/runnablelookuprows',
    runnableLookupRowsRangeDelete: 'admin/runnableLookupRows/deleteRange',
    RunnableLookupCells: 'admin/runnablelookupcells',
    runnableLookupDisplayColumnCheck: 'admin/runnablelookupcolumns/displayColumnCheck',
    runnableLookupHasUniqueIdentifier: 'admin/runnablelookups/hasUniqueIdentifier',
    dashboardWidget: 'dashboardWidget',
    widget: 'widget',
    performanceStandard: 'performancestandards',
    performanceStandardRequirement: 'performanceStandardRequirements',
    performanceStandardRequirementByPerformanceStandard: 'performanceStandardRequirements/performanceStandard',
    performanceStandardList: 'performancestandards/list',
    drawingPreview: 'drawingpreview',
    drawingInspectionDetails: 'drawinginspectiondetails',
    inspectionPointType: 'inspectionpointtype',
    masterAttributes: 'admin/masterAttributes',
    masterAttributesWithUnits: 'admin/masterAttributes/numeric-units',
    organizationMasterAttributes: 'admin/masterAttributes/organization',
    siteMasterAttributes: 'admin/masterAttributes/site',
    inspectionReportTemplatesMasterAttributes: 'admin/masterAttributes/inspectionReportTemplates',
    widgetParameter: 'widgetParameter',
    inspectionPointEquipmentData: 'inspectionPointEquipmentData',
    dateTypeAttributes: 'runnablemodelmetadata/getNodeAttributesOfTypeDateById',
    runnableModelRiskMatrix: 'runnablemodelmetadata/getRiskMatrix',
    runnableModelWhatIfDateAttribute: 'runnablemodelmetadata',
    hierarchyTemplateLookupValues: 'hierarchytemplatelookupvalues',
    protectingLocations: 'protectinglocations',
    physicalReliefDevice: {
      parent: 'physicalReliefDevice',
      getUninstalledDevices: 'physicalReliefDevice/getUninstalledPhysicalReliefDevices',
      getUninstalledDeviceCount: 'physicalReliefDevice/getUninstalledPhysicalReliefDeviceCount',
      getUninstallLocations: 'physicalReliefDevice/getUninstallLocations',
      getUninstallLocationCount: 'physicalReliefDevice/getUninstallLocationCount',
      installDevice: 'physicalReliefDevice/installDevice',
      uninstallDevice: 'physicalReliefDevice/uninstallDevice',
    },
    guildList: 'guidlist',
    equipmentFunctionModelSelection: 'equipmentfunctionmodelselection',
    equipmentFunctionModelSelectionById: 'equipmentfunctionmodelselection/byId',
    maintenanceStrategyLibrary: 'maintenancestrategylibrary',
    maintenanceStrategyBoundaryDiagram: 'maintenancestrategyboundarydiagram',
    runnableEquipmentFunctionModelSelection: 'runnableequipmentfunctionmodelselection',
    runnableEquipmentFunctionModelFunctions: 'runnableequipmentfunctionmodelselection/modelFunctions',
    rbiDataUpload: 'rbiDataUpload',
    dataUploadSites: 'dataUpload/sites',
    dataUploadExistingRecord: 'DataUploadExistingRecord',
    hierarchyTreeRoot: 'HierarchyTree/GetRootLevelAsync',
    hierarchyTreeChildren: 'HierarchyTree/GetChildrenAsync',
    hierarchyFilterNodes: 'HierarchyTree/FilterNodesAsync',
    hierarchyItemsCount: 'HierarchyTree/GetHierarchyItemsCountAsync',
    runnableEquipmentFunctionModel: 'runnableequipmentfunction/model',
    systemConfiguration: 'systemConfiguration',
    systemConfigurationGetAll: 'systemConfiguration/getAll',
    domainConfiguration: 'domainConfiguration',
    cloneRbiModel: 'cloneRbiModel',
    cloneRcmModel: 'cloneRcmModel',
    clientTranslation: 'clientTranslation',
    domainTerminology: 'domainTerminology',
    orgTreeNodeErpIdentifier: 'OrgTreeNodeErpIdentifier',
    userCatalog: 'userCatalog',
    pagination: {
      hasResults: 'hasResults',
      getCount: 'getCount',
      paginate: 'paginate',
    },
    options: {
      associatedEquipmentHierarchyTreeChildren: 'hierarchy/associatedEquipmentTreeOptionChildren',
      hierarchyTree: 'hierarchy/treeOptions',
      hierarchyTreeChildren: 'hierarchy/treeOptionChildren',
      inspectionReportTile: 'inspectionReportTileOptions',
      inspectionReportTree: 'inspectionReportTileOptions/treeOptions',
      maintenanceStrategyTileOptions: 'maintenanceStrategyTileOptions',
      maintenanceStrategyTileOptionById: 'maintenanceStrategyTileOptions/orgTreeNodeId',
      maintenanceStrategyTree: 'maintenanceStrategyTileOptions/tree',
      masterInspectionMethods: 'masterInspectionMethods/options',
      masterInspectionMethodTile: 'masterInspectionMethodTileOptions',
      modelNodes: 'modelNodes/options',
      orgTreeNode: 'hierarchy/siteChildren/options',
      reportHierarchyTree: 'report/treeOptions',
      reportHierarchyTreeChildren: 'report/treeOptionChildren',
      thicknessReportTile: 'thicknessReportTileOptions/thicknessReports',
      thicknessLocationTile: 'thicknessLocations',
      thicknessLocationTree: 'thicknessLocations/treeOptions',
      thicknessLocationTreeChildren: 'thicknessLocations/treeOptionChildren',
      inspectionReportsOrgTreeNode: 'inspectionReports/hierarchy/options',
      thicknessReportsOrgTreeNode: 'thicknessReports/hierarchy/options',
      thicknessLocationsOrgTreeNode: 'thicknessLocations/hierarchy/options',
      nodeInputSyncAlgorithms: 'syncAlgorithms/options',
      nodeInputSyncAlgorithmsDropdownOptions: 'syncAlgorithms/dropdownOptions',
      nodeInputsWithSyncAlgorithms: 'syncalgorithmsmodelmappings/nodeInputsByModelId',
      massCriticalityTemplatesAssetsHierarchyItems: 'massCriticalityTemplates/assets/hierarchy',
      massCriticalityTemplateCheckedAssetItems: 'massCriticalityTemplates/assets/tableItems',
      massCriticalityTemplateCheckedAssetItemsCnt: 'massCriticalityTemplates/assets/tableItems/cnt',
    },
    masterAttributeHierarchyTemplate: 'admin/hierarchyTemplateAttributes/masterAttribute',
    events: 'events',
    eventsLinkedItemParent: 'events/related-item/parent',
    eventsCount: 'events/count',
    eventViewTableCount: 'events/countViewTable',
    eventViewTable: 'events/paginateViewTable',
    removeEventLinkedItemsByParentId: 'events/removeLinkedItemsByParentId',
    eventProcesses: 'processes',
    eventProcessStages: 'process-stages',
    restrictionFilterByOrgTreeNode: 'restriction/filterByOrgTreeNode',
    auditSummaryData: 'audit/auditSummaryData',
    tableTemplateSave: 'admin/tableTemplate/save',
    tableTemplateColumns: 'admin/tableTemplateColumn',
    tableTemplateColumnMaxColumnIndex: 'admin/tableTemplateColumn/maxColumnIndex',
    hierarchyTemplateSave: 'admin/hierarchyTemplates/hierarchyTemplateSave',
    hierarchyTemplateUpdate: 'admin/hierarchyTemplates/hierarchyTemplateUpdate',
    // new security
    security: {
      roles: {
        root: 'roles',
        paginateTable: 'roles/paginateTable',
        paginateTableCount: 'roles/paginateTableCount',
      },
      userGroups: {
        userGroups: 'userGroup',
        userGroupSummary: 'userGroup/summary',
        userGroupSummaryCount: 'userGroup/summary/count',
        userGroupUsersSummary: 'userGroup/users/summary',
      },
      permissionTreeOptions: 'policies/permissionTreeNodes',
      roleAssignment: {
        root: 'roleAssignments',
        getByUserId: 'roleAssignments/user',
        resourceSummary: 'roleAssignments/resource-summary',
      },
      principals: {
        root: 'principals',
        group: 'principals/group',
      },
      usersGroups: 'usersGroups',
      resources: {
        treeNodes: 'resources/treeNodes',
        ancestorNodesIds: 'resources/ancestorNodesIds',
      },
      externalRoleAssignment: {
        root: 'externalRoleAssignments',
        getAllWithSelection: 'externalRoleAssignments/allWithSelection',
      },
    },
    identity: 'identity',
    auditEventApplication: 'audit/AuditApplicationOptions',
    libraryNodes: 'libraryNodes',
    libraryNodeLookups: 'libraryNode/Lookups',
    libraryNodesModelUsage: 'LibraryNode/ModelUsage',
    libraryNodeAttributes: 'LibraryNodesAttributes',
    deletelibraryNodeInputAttributes: 'LibraryNode/InputAttribute',
    deletelibraryNodeOutputAttributes: 'LibraryNode/OutputAttribute',
    auditReport: 'report/AuditReport',
  },
  constants: {
    emptyGuid: '00000000-0000-0000-0000-000000000000',
    runnableModelNodeRoundingDigits: 6,
    assessmentMethodMaintenanceId: '407ff705-8922-4b17-a768-d8a6d9c0ba85',
    assessmentMethodRbiId: '8e4d996d-d48f-4d7f-8fb7-f03ce6ed2631',
    noneDropdownValue: '',
    noneDropdownLabel: '(none)',
    opSumMitigationFilterTagId: '8bdad503-08be-4dce-afe3-3169669d9a8d',
    eventStageLookupId: '5d3c3973-941a-4fc5-99f9-bbd6ab5bef2f',
    events: {
      identifiers: {
        inspectionReportObservationInput: 'InspectionReportObservationInput',
        inspectionReport: 'InspectionReport',
      },
    },
    guidPattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    syncAlgorithms: {
      lastInspectionDateAlgorithmId: '77a23172-b7b6-43e5-98d4-ef5a6c98fee0',
      numberOfInspectionsAlgorithmId: '97f2f2bd-513c-481e-befa-062a96142feb',
      inspectionConfidenceAlgorithmId: '235d57ef-0320-4921-bdb7-ed3af75d21bf',
      avgLtCorrosionAlgorithmId: '3bf8bc35-ab55-42e1-9154-0e5959d06933',
      avgStCorrisionAlgorithmId: '7ab59f87-2d9b-4987-98df-a2a90760351f',
      maxLtCorriosionAlgorithmId: '9191f163-b226-454e-8423-b785afcb6df6',
      maxStCorriosionAlgorithmId: 'c674d13e-c8d2-41d6-adb8-380fbe6a05e2',
      highestInspectionConfidenceId: '5bd8376d-3c1d-40cc-84e9-f612e7ac521f',
      noOfInspectionsFromHighestInspectionId: '215e09d7-2a9c-4c7c-b8c5-c34a65b27ce0',
    },
  },
  fileExtensions: {
    lookupsPackage: '.aamlk',
    modelPackage: '.aamdl',
    rcmPackage: '.aarcm',
  },
  routes: {
    authLoginValidator: '/.auth/me',
    authLogin: '/.auth/login',
    authLogout: '/.auth/end-session',
    dashboard: '/dashboard',
    error: '/error',
    catalogerror: '/catalog-error',
    events: '/events',
    security: {
      parent: '/security',
      roles: '/security/roles',
      users: '/security/users',
      roleDetails: '/security/roles/:id/details',
    },
    securityNew: {
      parent: 'security-new',
      users: '/security-new/users',
      roles: '/security-new/roles',
      userGroups: '/security-new/user-groups',
      reseedPermission: '/security-new/re-seed-permission',
    },
    idms: {
      inspectionReportsShort: 'inspection-reports',
      inspectionReports: '/idms/inspection-reports/',
      inspectionReport: {
        root: (id: string) => `/idms/inspection-reports/${id}`,
        media: (id: string) => `/idms/inspection-reports/${id}/media`,
      },
      inspectionReportMedia: 'inspection-reports/:id/media',
      inspectionReportDrawing: {
        root: (id: string) => `/idms/drawings/${id}`,
      },
    },
    thicknessManagement: {
      thicknessReportsShort: 'thickness-reports',
      thicknessReports: '/thickness-management/thickness-reports/',
      thicknessReport: {
        root: (id: string) => `/thickness-management/thickness-reports/${id}`,
        media: (id: string) => `/thickness-management/thickness-reports/${id}/media`,
      },
      thicknessReportMedia: 'thickness-reports/:id/media',
      thicknessLocations: '/thickness-management/thickness-locations',
      thicknessLocationbyId: (id: string) => `/thickness-management/thickness-locations/${id}`,
      thicknessLocationsmedia: (id: string) => `/thickness-management/thickness-locations/${id}/media`,
    },
    mo: {
      maintenanceStrategies: '/analysis/maintenance',
      maintenanceOptimization: '/analysis/maintenance-optimization',
      maintenanceOptimizationAnalysis: '/analysis/maintenance-optimization/analysis',
      maintenanceOptimizationAdHoc: '/analysis/maintenance-optimization/adhoc',
      recommendations: {
        parent: '/analysis/recommendations',
        byModelRunId: (orgTreeNodeId: string, model: string) => `/analysis/recommendations/${orgTreeNodeId}/${model}`,
        media: (orgTreeNodeId: string, itemid: string) => `/analysis/recommendations/${orgTreeNodeId}/${itemid}/media`,
      },
    },
    hierarchy: {
      parent: '/hierarchy',
      item: {
        media: (orgTreeNodeId: string) => `/hierarchy/${orgTreeNodeId}/media`,
        mediabyId: (orgTreeNodeId: string, id: string) => `/hierarchy/${orgTreeNodeId}/media/${id}`,
        root: (itemid: string) => `/hierarchy/${itemid}`,
        equipment: (itemid: string) => `/hierarchy/${itemid}/protected-equipment`,
      },
    },
    admin: {
      organisations: '/admin/organizations/orgs',
      orgDetails: (orgId: string) => `/admin/organizations/orgs/${orgId}/details`,
      orgBySiteid: (siteid: string) => `/admin/organizations/${siteid}/sites`,
      orgDetailBySite: (orgId: string, siteid: string) => `/admin/organizations/${orgId}/sites/${siteid}/details`,
      orgMasterAttrs: (orgId: string) => `/admin/organizations/${orgId}/master-attributes`,
      neworganisation: '/admin/organizations/orgs/new/details',
      models: '/admin/models',
      riskMatrix: '/admin/risk-matrices',
      newRiskMatrix: {
        parent: '/admin/risk-matrix/new/details',
        ByMatrixId: (id: string) => `/admin/risk-matrix/${id}`,
        CanvasByMatrixId: (id: string) => `/admin/risk-matrix-canvas/${id}`,
      },
      dataUpload: '/admin/data-uploads',
      hierarchyTemplates: '/admin/hierarchy-templates',
      hierarchyTemplatesNew: '/admin/hierarchy-templates-new',
      inspectionReportTemplates: '/admin/inspection-report-templates',
      units: '/admin/units',
      unitsbyId: (id: string) => `/admin/units/${id}`,
      masterAttributes: '/admin/master-attributes',
      maintenance: {
        plannedMaintenance: '/planned-maintenance-library',
        plannedMaintenanceById: (id: string) => `/planned-maintenance-library/${id}`,
        performanceStandards: '/admin/performance-standards',
        performanceStandardsById: (id: string) => `/admin/performance-standards/${id}`,
        maintenanceStrategies: '/maintenance-strategies-library',
        maintenanceStrategiesById: (id: string) => `/maintenance-strategies-library/${id}`,
      },
      masterInspectionMethods: '/admin/master-inspection-methods',
      syncAlgorithms: '/admin/update-operations',
      lookups: '/admin/lookups',
      tableAttributeTemplate: '/admin/table-attribute-template',
      model: {
        details: (modelId: string) => `/admin/model/${modelId}/details`,
        riskMatrix: (modelId: string) => `/admin/model/${modelId}/risk-matrix`,
        mitigations: (modelId: string, modelName: string) => `/admin/model/${modelId}/mitigations/${modelName}`,
        template: (modelId: string, modelName: string) => `/admin/model/${modelId}/template-mappings/${modelName}`,
        functions: (modelId: string) => `/admin/model/${modelId}/functions`,
        units: (modelId: string, modelName: string) => `/admin/model/${modelId}/units/${modelName}`,
        syncAlgorithmMappings: {
          root: (modelId: string, modelName: string) => `/admin/model/${modelId}/${modelName}/update-operations-mappings`,
          syncAlgorithms: {
            root: (modelId: string, modelName: string) => `/admin/model/${modelId}/${modelName}/update-operations-mappings/update-operations`,
            item: (modelId: string, modelName: string, algorithmId: string) =>
              `/admin/model/${modelId}/${modelName}/update-operations-mappings/update-operations/${algorithmId}`,
          },
        },
      },
    },
    eventTemplate: {
      parent: '/admin/event-templates',
      processes: '/admin/event-processes',
    },
    rbiRiskAnalysis: {
      parent: '/analysis/rbi',
      analysis: {
        byId: (id: string) => `/analysis/rbi/${id}`,
        media: (itemid: string, orgTreeNodeId: string) => `/analysis/rbi/${itemid}/media/${orgTreeNodeId}`,
      },
      templates: '/analysis/massRiskCollections',
      results: '/analysis/massRiskResults',
      rbiProjection: '/analysis/riskProjection',
    },
    manageEquipment: {
      reports: '/reports',
      linkModels: '/assets/orglinkmodels',
    },
    build: {
      models: '/models',
      modelDetails: {
        addNewModel: '/models/new/details',
        details: (id: string, methodId: string) => `/models/${id}/details/${methodId}`,
        mitigations: (itemId: string, methodId: string) => `/models/${itemId}/mitigations/${methodId}`,
        nodes: (itemId: string, methodId: string) => `/models/${itemId}/nodes/${methodId}`,
        functions: (itemId: string, methodId: string) => `/models/${itemId}/functions/${methodId}`,
      },
      functions: '/functions',
      newfunction: '/functions/new/details',
      function: {
        byId: (id: string) => `/functions/${id}`,
        body: (id: string) => `/functions/${id}/body`,
      },
      lookups: '/lookups',
      lookupsOld: '/lookups-old',
      libraryNodes: '/library-nodes',
      nodes: {
        attributes: (id: string) => `/nodes/${id}/attributes`,
      },
    },
  },
  HttpStatus: require('http-status-codes'),
};

export const MITIGATIONS_SERVICE = new InjectionToken<MitigationsService>('MitigationsService2');
